import { CheckCircle, CheckCircleOutline } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { queryClient } from '../../../../../services/react-query/queryClient';
import { queryKeys } from '../../../../../services/react-query/queryKeys';
import { useGetTaskById } from '../../../../../services/react-query/tasks';
import { useUpdateTaskStep } from '../../../../../services/react-query/tasks/useUpdateTaskStep';
import { Step } from '../../../../../types/Step';
import { humanizeDate, humanizeTimestamp } from '../../../../../utility/dateUtils';
import TaskDuePill from '../../../../tasks/TaskDuePill';
import TaskStatusPill from '../../../../tasks/TaskStatusPill';
import DrawerSection from '../DrawerSection';
import InfoRow from '../InfoRow';
import ModuleDrawerHeader from '../ModuleDrawerHeader';
import { moduleDrawerStyles } from '../moduleDrawerStyles';
import _ from 'lodash';

const CenterSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
    <CircularProgress size={20} />
  </Box>
);

const TaskDrawerView: React.FC<{ uuid: string }> = ({ uuid }) => {
  const { data: task, isLoading } = useGetTaskById({ uuid });
  const { mutate: updateStep } = useUpdateTaskStep();

  const [steps, setSteps] = useState<Step[]>([]);

  useEffect(() => {
    setSteps(task?.steps ?? []);
  }, [task]);

  const sortedSteps = useMemo(() => [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [steps]);

  const handleStepToggle = useCallback(
    (step: Step, action: 'start' | 'complete') => {
      const started = !step.started_at;
      const completed = !step.completed_at;
      updateStep(
        {
          uuid: step.uuid,
          step_action: action,
          started: action === 'start' ? started : false,
          completed: action === 'complete' ? completed : false,
        },
        {
          onSuccess: (updatedStep: Step) => {
            setSteps((prev) => prev.map((s) => (s.uuid === updatedStep.uuid ? updatedStep : s)));
            queryClient.invalidateQueries({ queryKey: [queryKeys.allTasks, uuid] });
          },
        }
      );
    },
    [updateStep]
  );

  if (isLoading) return <CenterSpinner />;
  if (!task) {
    return (
      <Typography variant="body2" color="error">
        Task not found
      </Typography>
    );
  }

  return (
    <>
      <ModuleDrawerHeader>{_.truncate(task.name)}</ModuleDrawerHeader>
      <Box sx={moduleDrawerStyles.content}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Task Information */}
          <DrawerSection title="Task Information">
            <InfoRow label="Name" value={task.name} />
            <InfoRow label="Account" value={task.account_name} />
            <InfoRow label="Event" value={task.event_display_name} />
            <InfoRow label="Task Type" value={task.type_value} />
            <InfoRow label="Assigned To" value={task.employee_display_name} />
            <InfoRow label="Start Date" value={humanizeDate(task.start_date)} />
            <InfoRow label="Due Date" value={humanizeDate(task.due_date)} rightNode={<TaskDuePill task={task} />} />
            <InfoRow label="Status" value={task.status} rightNode={<TaskStatusPill status={task.status} />} />
          </DrawerSection>

          {/* Steps */}
          <DrawerSection title="Steps">
            <TableContainer component={Paper} sx={{ pt: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Step Name</TableCell>
                    <TableCell sx={{ width: 240 }}>Start</TableCell>
                    <TableCell sx={{ width: 240 }}>Complete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSteps.map((step) => {
                    const started = Boolean(step.started_at);
                    const completed = Boolean(step.completed_at);

                    return (
                      <TableRow key={step.uuid}>
                        <TableCell>{step.name}</TableCell>

                        {/* Start Button */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!completed && (
                              <IconButton
                                onClick={() => handleStepToggle(step, 'start')}
                                sx={{
                                  border: '1px solid',
                                  borderColor: started ? '#FF9D3D' : '#E3E3E5',
                                  bgcolor: started ? '#FFF5EC' : '#fff',
                                  borderRadius: '6px',
                                  width: 36,
                                  height: 36,
                                }}
                                aria-label="toggle start"
                              >
                                {started ? <CheckCircle sx={{ color: '#FF9D3D' }} /> : <CheckCircleOutline sx={{ color: '#9AA0A6' }} />}
                              </IconButton>
                            )}

                            <Box>
                              {step.started_by_name && (
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  {step.started_by_name}
                                </Typography>
                              )}
                              {step.started_at && (
                                <Typography variant="caption" color="text.secondary">
                                  {humanizeTimestamp(step.started_at)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Complete Button */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              onClick={() => handleStepToggle(step, 'complete')}
                              sx={{
                                border: '1px solid',
                                borderColor: completed ? '#35B23D' : '#E3E3E5',
                                bgcolor: completed ? '#E8F5E9' : '#fff',
                                borderRadius: '6px',
                                width: 36,
                                height: 36,
                              }}
                              aria-label="toggle complete"
                            >
                              {completed ? <CheckCircle sx={{ color: '#35B23D' }} /> : <CheckCircleOutline sx={{ color: '#9AA0A6' }} />}
                            </IconButton>

                            <Box>
                              {step.completed_by_name && (
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  {step.completed_by_name}
                                </Typography>
                              )}
                              {step.completed_at && (
                                <Typography variant="caption" color="text.secondary">
                                  {humanizeTimestamp(step.completed_at)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {sortedSteps.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          No steps available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DrawerSection>
        </Box>
      </Box>
    </>
  );
};

export default TaskDrawerView;
